import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Spinner, Alert, Offcanvas } from 'react-bootstrap';
import ReactFlow, { Background, Controls, MarkerType } from 'reactflow';
import 'reactflow/dist/style.css';
import { FaFileExport } from 'react-icons/fa';
import { runbookVersionService } from '../../services/runbookVersion.service';
import StepNode from '../workflow/nodes/StepNode';
import { NodeDisplayContext } from '../workflow/nodes/NodeDisplayContext';
import { buildConnectorColorMap } from '../workflow/nodes/connectorColor';
import './RamBotRunbookViewer.css';

// A large, genuinely non-editable popup for "open this runbook" from RamBot (#2448) -- deliberately
// NOT a readOnly mode bolted onto WorkflowBuilder (3700+ lines of edit-state/drag/drop/Redux
// wiring); instead it fetches the same already-stored RUNBOOKSTRING export already uses (GET
// /api/runbook-version) and renders it on ReactFlow with `nodesDraggable`/`nodesConnectable` off.
// Nothing here can mutate or save a runbook -- there is no Apply/save path at all, only Export
// (identical connector-credential safety as WorkflowBuilder's own Export Workflow / RamBot's export).
//
// Reuses WorkflowBuilder's own StepNode (+ NodeDisplayContext/connectorColor) so a step looks
// exactly like it does in the real designer, rather than a bespoke box -- StepNode is a pure,
// self-contained display component (no edit-state dependency), so it's safe to import here.

// Mirrors WorkflowBuilder.jsx's `actionToStepType`/`actionToLabel` (defined inline there, not
// exported -- RUNBOOKSTRING stores `action` as the numeric StepAction enum value). Keep these in
// sync if that mapping changes.
const ACTION_TO_STEP_TYPE = {
  0: 'executeRunbook', 1: 'query', 2: 'lookup', 3: 'create', 4: 'update', 5: 'delete',
  6: 'ifElse', 8: 'forEach', 10: 'setVar', 24: 'jsonSetVar', 11: 'getPushedData',
  12: 'executeStoredProcedure', 14: 'declareVar', 15: 'logMessage', 16: 'setError',
  17: 'endLoop', 18: 'sendEmail', 19: 'upsertClientVar', 20: 'buildTableCache',
  21: 'lookupTableCache', 22: 'queryTableCache', 23: 'deleteTableCache',
};
const ACTION_TO_LABEL = {
  0: 'Execute Runbook', 1: 'Query', 2: 'Lookup', 3: 'Create', 4: 'Update', 5: 'Delete',
  6: 'If/Else', 8: 'For Each', 10: 'Set Variable', 24: 'JSON Set Variable', 11: 'Get Pushed Data',
  12: 'Execute Stored Procedure', 14: 'Declare Variable', 15: 'Log Message', 16: 'Set Error',
  17: 'End Loop', 18: 'Send Email', 19: 'Upsert Client Variable', 20: 'Build Table Cache',
  21: 'Lookup Table Cache', 22: 'Query Table Cache', 23: 'Delete Table Cache',
};
const CRUD_STEP_TYPES = ['query', 'create', 'update', 'delete', 'lookup', 'executeStoredProcedure'];
const actionName = (n) => ACTION_TO_LABEL[n] ?? `Action ${n}`;

// Scalar fields worth surfacing in the details panel, in display order -- skipped when blank/falsy.
// Deliberately excludes bookkeeping fields (runbookId, groupId, parentStepId, position*) that are
// either redundant here or meaningless outside the designer's own canvas state.
const DETAIL_FIELDS = [
  ['system', 'System'],
  ['systemObject', 'System object'],
  ['connectorNumber', 'Connector'],
  ['fieldsToQuery', 'Fields to query'],
  ['filter', 'Filter'],
  ['condition', 'Condition'],
  ['conditionTruePath', 'If true → step'],
  ['conditionFalsePath', 'If false → step'],
  ['dataStep', 'Iterates data from'],
  ['nextStepId', 'Next step'],
  ['errorStepId', 'On error → step'],
  ['customErrorMessage', 'Custom error message'],
];
const DETAIL_FLAGS = [
  ['netChange', 'Net change only'],
  ['serialProcessing', 'Serial processing'],
  ['inspectRequest', 'Inspect request'],
  ['errorUponCompletion', 'Error upon completion'],
];

// Builds the exact same node.data shape WorkflowBuilder's own import (handleImportWorkflow)
// builds for a step, so StepNode renders identically to the real designer -- same icon, same
// connector-color accent, same "Query - Intacct" style fallback title, same connector pill.
function stepNodeData(s) {
  const stepType = ACTION_TO_STEP_TYPE[s.action] || 'query';
  const stepLabel = ACTION_TO_LABEL[s.action] || 'Query';
  const displayLabel = s.stepName
    || (CRUD_STEP_TYPES.includes(stepType) && s.system ? `${stepLabel} - ${s.system}` : stepLabel);
  return {
    stepId: s.stepNumber,
    label: displayLabel,
    stepType,
    description: s.stepDescription || '',
    connectorId: s.connectorId || '',
    connectorNumber: s.connectorNumber || '',
    systemObject: s.systemObject || '',
    dataStepId: s.dataStepId || s.dataStep || '',
    step: s, // the raw step, for the details panel
  };
}

// Steps carry their own canvas positionX/positionY (as authored in the designer), so this can
// reuse them directly instead of re-deriving a layout -- only very old rows lack them, in which
// case fall back to a simple vertical stack (ordered by stepOrder) so nothing collapses onto (0,0).
function layoutNodes(steps) {
  const sorted = [...steps].sort((a, b) => (a.stepOrder ?? 0) - (b.stepOrder ?? 0));
  return sorted.map((s, i) => {
    const hasPos = Number.isFinite(s.positionX) && Number.isFinite(s.positionY);
    return {
      id: s.stepNumber,
      position: hasPos ? { x: s.positionX, y: s.positionY } : { x: 80, y: i * 140 },
      data: stepNodeData(s),
      type: 'stepNode',
      draggable: false,
    };
  });
}

// Every control-flow link a step can carry, using the same sourceHandle ids StepNode itself
// renders (next/true/false/error) so an edge attaches to the right side of the node, exactly as
// in the real designer. If/Else nodes have no "next" handle -- only true/false -- and (mirroring
// WorkflowBuilder's own import) fall back to nextStepId as the true-path when conditionTruePath
// isn't set. A loop's data source is conveyed by the ordinary chain edge into it (same as the
// designer), not a separate edge.
function buildEdges(steps) {
  const ids = new Set(steps.map((s) => s.stepNumber));
  const arrow = { type: MarkerType.ArrowClosed, width: 18, height: 18, color: '#6c757d' };
  const edges = [];
  for (const s of steps) {
    const stepType = ACTION_TO_STEP_TYPE[s.action] || 'query';
    if (stepType === 'ifElse') {
      const truePath = s.conditionTruePath || s.nextStepId || '';
      if (truePath && ids.has(truePath)) {
        edges.push({ id: `${s.stepNumber}-true-${truePath}`, source: s.stepNumber, sourceHandle: 'true', target: truePath, label: 'Yes', style: { stroke: '#10b981', strokeWidth: 1.5 }, markerEnd: { ...arrow, color: '#10b981' } });
      }
      if (s.conditionFalsePath && ids.has(s.conditionFalsePath)) {
        edges.push({ id: `${s.stepNumber}-false-${s.conditionFalsePath}`, source: s.stepNumber, sourceHandle: 'false', target: s.conditionFalsePath, label: 'No', style: { stroke: '#f59e0b', strokeWidth: 1.5 }, markerEnd: { ...arrow, color: '#f59e0b' } });
      }
    } else if (s.nextStepId && ids.has(s.nextStepId)) {
      edges.push({ id: `${s.stepNumber}-next-${s.nextStepId}`, source: s.stepNumber, sourceHandle: 'next', target: s.nextStepId, markerEnd: arrow, style: { stroke: '#6c757d', strokeWidth: 1.5 } });
    }
    if (s.errorStepId && ids.has(s.errorStepId)) {
      edges.push({ id: `${s.stepNumber}-error-${s.errorStepId}`, source: s.stepNumber, sourceHandle: 'error', target: s.errorStepId, label: 'on error', style: { stroke: '#ef4444', strokeDasharray: '4 3' }, markerEnd: { ...arrow, color: '#ef4444' } });
    }
  }
  return edges;
}

// Stable reference -- ReactFlow warns/re-renders expensively if nodeTypes changes every render.
const NODE_TYPES = { stepNode: StepNode };

// A field-mapping entry's shape varies a bit by step type, but always carries a FieldName + value
// (plus RunbookId/StepId bookkeeping that's redundant to show here).
function fieldMappingLine(m, i) {
  const name = m.FieldName || m.fieldName || `Field ${i + 1}`;
  const value = m.value ?? m.Value ?? '';
  return `${name}: ${value}`;
}

function StepDetailsPanel({ step, onHide }) {
  return (
    <Offcanvas show={!!step} onHide={onHide} placement="end" backdrop={false} className="ram-bot-viewer-details">
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>
          {step?.stepNumber} <span className="ram-bot-viewer-readonly-badge">View only</span>
        </Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>
        {step && (
          <>
            <div className="ram-bot-viewer-details-title">{actionName(step.action)}</div>
            {step.stepDescription && <p className="ram-bot-viewer-details-desc">{step.stepDescription}</p>}
            <dl className="ram-bot-viewer-details-list">
              {DETAIL_FIELDS.filter(([key]) => !!step[key]).map(([key, label]) => (
                <React.Fragment key={key}>
                  <dt>{label}</dt>
                  <dd>{String(step[key])}</dd>
                </React.Fragment>
              ))}
              {DETAIL_FLAGS.filter(([key]) => !!step[key]).map(([key, label]) => (
                <React.Fragment key={key}>
                  <dt>{label}</dt>
                  <dd>Yes</dd>
                </React.Fragment>
              ))}
            </dl>
            {step.fieldMappings?.length > 0 && (
              <>
                <div className="ram-bot-viewer-details-title">Field mappings</div>
                <ul className="ram-bot-viewer-details-mappings">
                  {step.fieldMappings.map((m, i) => (
                    <li key={i}>{fieldMappingLine(m, i)}</li>
                  ))}
                </ul>
              </>
            )}
          </>
        )}
      </Offcanvas.Body>
    </Offcanvas>
  );
}

function RamBotRunbookViewer({ show, runbookId, runbookGuid, onHide, onExport, exporting }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [runbook, setRunbook] = useState(null); // parsed RUNBOOKSTRING
  const [selectedStep, setSelectedStep] = useState(null);

  useEffect(() => {
    if (!show || !runbookId || !runbookGuid) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setRunbook(null);
    setSelectedStep(null);

    (async () => {
      try {
        const response = await runbookVersionService.get({ runbookId, runbookGuid });
        const versions = response.data;
        // Current version = highest VersionNum (#1977 Apply model) -- same as export.
        const current = (versions || []).reduce(
          (max, v) => (max === null || v.versionNum > max.versionNum ? v : max),
          null
        );
        if (!current?.runbookString) throw new Error('No runbook definition found.');
        if (!cancelled) setRunbook(JSON.parse(current.runbookString));
      } catch (err) {
        if (!cancelled) {
          const serverMessage = err?.response?.data?.error || err?.response?.data?.message;
          setError(serverMessage || err?.message || `Could not load ${runbookId}.`);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [show, runbookId, runbookGuid]);

  const nodes = useMemo(() => (runbook ? layoutNodes(runbook.steps || []) : []), [runbook]);
  const edges = useMemo(() => (runbook ? buildEdges(runbook.steps || []) : []), [runbook]);
  // Same connector-color-by-identity + tooltip info StepNode uses on the real canvas.
  const connectorColors = useMemo(() => buildConnectorColorMap(runbook?.connectors), [runbook]);
  const connectorInfo = useMemo(() => {
    const map = {};
    (runbook?.connectors || []).forEach((c) => {
      if (c.connectorNumber) map[c.connectorNumber] = c;
      if (c.connectorId) map[c.connectorId] = c;
    });
    return map;
  }, [runbook]);

  return (
    <Modal show={show} onHide={onHide} dialogClassName="ram-bot-viewer-dialog" centered>
      <Modal.Header closeButton className="ram-bot-viewer-header">
        <Modal.Title>
          {runbookId} <span className="ram-bot-viewer-readonly-badge">View only</span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="ram-bot-viewer-body">
        {loading && (
          <div className="ram-bot-viewer-status">
            <Spinner animation="border" size="sm" className="me-2" />
            Loading {runbookId}…
          </div>
        )}
        {error && <Alert variant="danger" className="m-3">{error}</Alert>}
        {!loading && !error && runbook && (
          <>
            {runbook.connectors?.length > 0 && (
              <div className="ram-bot-viewer-connectors">
                {runbook.connectors.map((c) => (
                  <span key={c.connectorId} className="ram-bot-viewer-connector-chip">
                    {c.system}
                    {c.description ? ` — ${c.description}` : ''}
                  </span>
                ))}
              </div>
            )}
            <div className="ram-bot-viewer-canvas">
              <NodeDisplayContext.Provider value={{ connectorColors, connectorInfo }}>
                <ReactFlow
                  nodes={nodes}
                  edges={edges}
                  nodeTypes={NODE_TYPES}
                  nodesDraggable={false}
                  nodesConnectable={false}
                  onNodeClick={(_, node) => setSelectedStep(node.data.step)}
                  panOnScroll
                  zoomOnScroll
                  fitView
                  fitViewOptions={{ padding: 0.2 }}
                  proOptions={{ hideAttribution: true }}
                >
                  <Background gap={16} />
                  <Controls showInteractive={false} />
                </ReactFlow>
              </NodeDisplayContext.Provider>
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer className="ram-bot-viewer-footer">
        <span className="ram-bot-viewer-footer-note">View only — connector credentials are never included.</span>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={() => onExport(runbookId, runbookGuid)}
          disabled={exporting || !runbook}
        >
          <FaFileExport className="me-1" /> {exporting ? 'Exporting…' : 'Export JSON'}
        </Button>
        <Button variant="secondary" size="sm" onClick={onHide}>Close</Button>
      </Modal.Footer>
      <StepDetailsPanel step={selectedStep} onHide={() => setSelectedStep(null)} />
    </Modal>
  );
}

export default RamBotRunbookViewer;
